/**
 * Payer Connections staff roster — pure row-building logic (GDN-3408,
 * AC-13 to AC-17).
 *
 * Bridges the generated cross-organization live read
 * (`CrossOrganizationLiveEnrollmentsResponse`, GDN-3403) to the shared
 * `roster.ts`/`fold.ts` helpers from `@features/payer-connections`
 * (GDN-3400). Kept as a standalone pure module (no React, no RTK Query) so
 * the filter/sort/error-row behavior is directly unit-testable against a
 * fixture response, independent of the query hook.
 *
 * `submittedClaimCount` is hardcoded to 0 for every organization: this
 * cross-org read carries no per-org claim count (that is Slice C), so a
 * payer folds at most to `ready` ("Ready to bill") here — never `live`.
 * The "hide organizations whose payers are all Live" rule (AC-13) is still
 * implemented generically via `buildRosterRow`'s payers list, so it is
 * exercised the same way a future claim-count-aware read would exercise it;
 * it just never fires against this endpoint's real responses.
 */
import type {
  CrossOrganizationLiveEnrollmentsResponse,
  LiveEnrollmentResponse,
  OrganizationLiveEnrollmentsResponse,
} from '@store/generated/admin';

import { buildRosterRow, sortRoster } from '@features/payer-connections';
import type { BlockedParty, EnrollmentRow, FoldedPayer, RosterRow } from '@features/payer-connections';

/** submittedClaimCount is always 0 in this slice — see module doc. */
const SUBMITTED_CLAIM_COUNT = 0;

/** One row for the staff roster table: either a folded org (AC-14–AC-17) or a live-read error (AC-13). */
export interface RosterTableRow {
  organizationId: string;
  organizationName: string;
  isError: boolean;
  errorMessage: string | null;
  /** null only for error rows — there is nothing to fold. */
  blockedParty: BlockedParty | null;
  daysBlocked: number | null;
  payers: FoldedPayer[];
  liveCount: number;
}

function toEnrollmentRow(row: LiveEnrollmentResponse): EnrollmentRow {
  return {
    stedi_enrollment_id: row.stedi_enrollment_id,
    payer_name: row.payer_name ?? '',
    stedi_payer_id: row.stedi_payer_id ?? '',
    transaction_type: row.transaction_type,
    status: row.status,
    status_message: row.status_message,
    rejection_reason: row.rejection_reason,
    submitted_at: row.submitted_at,
    created_at: row.created_at,
    status_updated_at: row.status_updated_at,
    portal_url: row.portal_url,
  };
}

function toRosterTableRow(rosterRow: RosterRow): RosterTableRow {
  return {
    organizationId: rosterRow.organizationId,
    organizationName: rosterRow.organizationName,
    isError: false,
    errorMessage: null,
    blockedParty: rosterRow.blockedParty,
    daysBlocked: rosterRow.daysBlocked,
    payers: rosterRow.payers,
    liveCount: rosterRow.liveCount,
  };
}

function errorTableRow(org: OrganizationLiveEnrollmentsResponse): RosterTableRow {
  return {
    organizationId: org.organization_id,
    organizationName: org.organization_name,
    isError: true,
    errorMessage: org.error ?? 'The live read failed for this organization.',
    blockedParty: null,
    daysBlocked: null,
    payers: [],
    liveCount: 0,
  };
}

/**
 * Build the roster's rows from the cross-organization live read (AC-13):
 * one row per organization with ≥1 enrollment row and ≥1 non-Live payer.
 * Hides the internal test organization and organizations whose payers are
 * all Live. An organization with `error` set always renders — it is never
 * dropped by the enrollment/Live filters, which don't apply to it.
 *
 * `claimCountByOrgId` defaults every organization to 0 (see module doc) and
 * exists mainly so the "all Live payers are hidden" filter — which this
 * slice's real responses can never trigger, since a claim count of 0 never
 * folds a payer to `live` — is still directly testable. Production callers
 * should not pass it.
 */
export function buildPayerConnectionsRosterWithClaimCounts(
  response: CrossOrganizationLiveEnrollmentsResponse | undefined,
  asOf: Date,
  claimCountByOrgId: (organizationId: string) => number = () => SUBMITTED_CLAIM_COUNT,
): RosterTableRow[] {
  const organizations = response?.organizations ?? [];

  const errorRows: RosterTableRow[] = [];
  const foldedRows: RosterRow[] = [];

  for (const org of organizations) {
    if (org.is_internal_test) continue;

    if (org.error) {
      errorRows.push(errorTableRow(org));
      continue;
    }

    const rows = (org.enrollments ?? []).map(toEnrollmentRow);
    if (rows.length === 0) continue;

    const rosterRow = buildRosterRow(
      {
        organizationId: org.organization_id,
        organizationName: org.organization_name,
        rows,
        submittedClaimCount: claimCountByOrgId(org.organization_id),
      },
      asOf,
    );

    // "One or more non-Live payers" — an org whose payers are all Live folds
    // to an empty payers list and is hidden.
    if (rosterRow.payers.length === 0) continue;

    foldedRows.push(rosterRow);
  }

  const sortedFolded = sortRoster(foldedRows).map(toRosterTableRow);
  const sortedErrors = errorRows
    .slice()
    .sort((a, b) => a.organizationName.localeCompare(b.organizationName));

  // Error rows carry no blocked-on party to sort by (AC-17 sorts on it), so
  // they are appended after every party-ranked row rather than interleaved.
  return [...sortedFolded, ...sortedErrors];
}

/**
 * Page-level "Checked with the clearinghouse at <time>" line. Time-only when
 * `checkedAt` falls on the same calendar day as `now` (the common case —
 * staff load this page and immediately see a fresh read); date+time
 * otherwise, so a stale response is still legible as stale.
 */
export function formatCheckedAtLine(checkedAt: string | undefined, now: Date): string {
  if (!checkedAt) return '';
  const checked = new Date(checkedAt);
  if (Number.isNaN(checked.getTime())) return '';

  const sameDay =
    checked.getFullYear() === now.getFullYear() &&
    checked.getMonth() === now.getMonth() &&
    checked.getDate() === now.getDate();

  const formatted = sameDay
    ? checked.toLocaleTimeString()
    : checked.toLocaleString();

  return `Checked with the clearinghouse at ${formatted}`;
}

/** Production entry point: submittedClaimCount is always 0 in this slice (see module doc). */
export function buildPayerConnectionsRoster(
  response: CrossOrganizationLiveEnrollmentsResponse | undefined,
  asOf: Date,
): RosterTableRow[] {
  return buildPayerConnectionsRosterWithClaimCounts(response, asOf);
}
